import { Injectable, Injector, ElementRef, Component } from '@angular/core';

import {
  OverlayRef,
  Overlay,
  OverlayConfig,
  PositionStrategy,
  ConnectedOverlayPositionChange
} from '@angular/cdk/overlay';

import { ComponentPortal } from '@angular/cdk/portal';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromEvent'

@Injectable()
export class OverlayService {
  overlayRef: OverlayRef;
  overlayArrow: HTMLElement;

  constructor(
    private overlay: Overlay
  ) { }

  /**
   * Open the overlay
   * @param hostElement The element to tether the overlay to
   * @param overlayComponent The overlay component
   * @param injector Injector that will be availible to the overlay component
   */
  open(hostElement: ElementRef, overlayComponent: any, injector?: Injector, options?) {
    if (this.overlayRef && this.overlayRef.overlayElement) {
      this.overlayRef.dispose();
    }

    // Create an overlay that attaches to the host element (the input box)
    this.overlayRef = this.overlay.create(this.createOverlayConfig(hostElement, options));

    // Create ComponentPortal passing the injector for the input.
    const componentPortal = new ComponentPortal(overlayComponent, null, injector);

    // The component portal can now be attached to the overlay ref.
    this.overlayRef.attach(componentPortal);

    Observable.fromEvent(window, 'scroll').subscribe(() => {
      this.overlayRef.updatePosition();
    });

    return this.overlayRef;
  }

  close() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  opened(): boolean {
    if (this.overlayRef) {
      return !!this.overlayRef.overlayElement;
    } else {
      return false;
    }
  }

  private createOverlayConfig(element, options?) {
    return new OverlayConfig({
      positionStrategy: this.createPositionStrategy(element, options),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      panelClass: 'cf-popover'
    });
  }

  /**
   * Using same config as material for now
   * https://github.com/angular/material2/blob/master/src/lib/datepicker/datepicker.ts
   */
  private createPositionStrategy(element, options?): PositionStrategy {
    let positionStrategy;

    if (options && options.position && options.position === 'top') {
      positionStrategy = this.overlay.position()
        .connectedTo(element,
          { originX: 'center', originY: 'top' },
          { overlayX: 'center', overlayY: 'bottom' }
        )
        .withFallbackPosition(
          { originX: 'center', originY: 'bottom' },
          { overlayX: 'center', overlayY: 'top' },
          undefined
        )
        .withFallbackPosition(
          { originX: 'start', originY: 'top' },
          { overlayX: 'start', overlayY: 'bottom' }
        )
        .withFallbackPosition(
          { originX: 'start', originY: 'bottom' },
          { overlayX: 'start', overlayY: 'top' },
          undefined
        )
        .withFallbackPosition(
          { originX: 'end', originY: 'top' },
          { overlayX: 'end', overlayY: 'bottom' }
        )
        .withFallbackPosition(
          { originX: 'end', originY: 'bottom' },
          { overlayX: 'end', overlayY: 'top' },
          undefined
        );
    } else {
      positionStrategy = this.overlay.position()
        .connectedTo(element,
          { originX: 'center', originY: 'bottom' },
          { overlayX: 'center', overlayY: 'top' }
        )
        .withFallbackPosition(
          { originX: 'center', originY: 'top' },
          { overlayX: 'center', overlayY: 'bottom' },
          undefined
        )
        .withFallbackPosition(
          { originX: 'start', originY: 'bottom' },
          { overlayX: 'start', overlayY: 'top' }
        )
        .withFallbackPosition(
          { originX: 'start', originY: 'top' },
          { overlayX: 'start', overlayY: 'bottom' },
          undefined
        )
        .withFallbackPosition(
          { originX: 'end', originY: 'bottom' },
          { overlayX: 'end', overlayY: 'top' }
        )
        .withFallbackPosition(
          { originX: 'end', originY: 'top' },
          { overlayX: 'end', overlayY: 'bottom' },
          undefined
        );
    }


    /**
     * The follwing will subscribe to position changes and will update the classes/styles on the
     * overlay in order to properly position the arrow.
     */
    positionStrategy.onPositionChange.subscribe((pos: ConnectedOverlayPositionChange) => this.calculateArrowPosition(pos, element));

    return positionStrategy;
  }

  /**
   * ensures the arrow is position properly
   */
  private calculateArrowPosition(pos, element) {
    // if the arrow has not been created yet...
    if (!this.overlayRef.overlayElement.querySelector('.cf-popover-arrow')) {
      // create the arrow element
      this.overlayArrow = document.createElement('div');
      // add the arrow class to render an arrow
      this.overlayArrow.classList.add('cf-popover-arrow');
      // append the arrow to the overlay
      this.overlayRef.overlayElement.appendChild(this.overlayArrow);
    }

    // remove any old position classes and add the current one
    // this will position the arrow on the top/bottom of the overlay
    this.overlayRef.overlayElement.className = this.overlayRef.overlayElement.className.replace(/position-.*/g, '');
    this.overlayRef.overlayElement.classList.add(`position-${pos.connectionPair.originY}`);

    const elemWidth = element.nativeElement.offsetWidth;
    const overlayWidth = this.overlayRef.overlayElement.offsetWidth;

    // if the overlay is bounded to the end of the element and
    // the overlay is wider then the elemnet then the arrow
    // needs to be centered on the element
    if (
      pos.connectionPair.originX === 'end' &&
      pos.connectionPair.overlayX === 'end' &&
      elemWidth < overlayWidth
    ) {
      const arrowOffsetRight = (overlayWidth / 2) - (element.nativeElement.offsetWidth / 2) + 'px';
      this.overlayArrow.style.transform = `translate3D(${arrowOffsetRight}, 0, 0)`;

      // else if the overlay is bounded to the start of the element and
      // the overlay is wider then the elemnet then the arrow
      // needs to be centered on the element
    } else if (
      pos.connectionPair.originX === 'start' &&
      pos.connectionPair.overlayX === 'start' &&
      elemWidth < overlayWidth
    ) {
      const arrowOffsetLeft = (overlayWidth / 2) - (element.nativeElement.offsetWidth / 2) + 'px';
      this.overlayArrow.style.transform = `translate3D(-${arrowOffsetLeft}, 0, 0)`;

      // else just center the arrow
    } else {
      this.overlayArrow.style.transform = 'translate3D(0, 0, 0)';
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/datepicker/shared/overlay.service.ts